import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';
import { css } from 'linaria';
import { Drawer, DrawerHeader, DrawerContent } from 'fronton-react/drawer';
import { Remote } from '@okapi/remote';
import { RootReducer } from 'store/root-reducer';
import { getArticle } from 'common/selectors';
import { CALCULATE_QUANTITY_GA_CLOSE } from 'features/ga-events/messages/ga-calculate-quantity-close.pdp';
import {
  getDisplayedName,
  getPrimaryPrice,
  getSecondPrice,
  getProductImages,
  getIsPricesSwapped,
  GO_BACK_KEY,
  CLOSE_MODAL_KEY,
} from 'features/product-info';
import { getRegionId } from 'features/page-model/ducks/get-model';
import { Fallback } from './fallback';
import { Breakpoint } from 'types/utils';
import { addMediaQueryHandlers } from 'utils/add-media-query-handlers';
import { MediaQueries } from 'utils/internal-specifics/media-queries';
import { Modal, ModalContent, ModalHeader } from 'fronton-react';
import { useTranslation } from 'react-i18next';

type TData = {
  productId: string;
  displayedName: string | null;
  photo: string | null;
  mainPrice: number | null;
  additionalPrice: number | null;
  mainUomRus: string | null;
  additionalUomRus: string | null;
};

type CalculatorModalMappedState = {
  data: TData;
};

export type CalculatorModalProps = {
  isShowed: boolean;
  onCloseModal: () => void;
  gaCalculateQuantityClose: ({ elementText }: { elementText: string }) => void;
} & CalculatorModalMappedState;

export const CalculatorModal: React.FC<CalculatorModalProps> = ({
  isShowed,
  onCloseModal,
  data,
  gaCalculateQuantityClose,
}) => {
  const { t } = useTranslation();
  const [breakpoint, setBreakpoint] = useState<Breakpoint>('large');

  const handleOnClose = (elementText: string) => {
    gaCalculateQuantityClose({ elementText });
    onCloseModal();
  };

  useEffect(
    () =>
      addMediaQueryHandlers(
        [
          MediaQueries.LARGE_DESK_SCREEN,
          MediaQueries.MEDIUM_DESK_SCREEN,
          MediaQueries.TABLET_SCREEN,
          MediaQueries.MOBILE_SCREEN,
        ],
        ['large', 'medium', 'tablet', 'mobile'],
        'large',
        setBreakpoint,
      ),
    [],
  );

  const isMobile = breakpoint === 'mobile';

  return isMobile ? (
    <Modal
      modalID="pdp-calculator-modal"
      onClose={() => handleOnClose(t(CLOSE_MODAL_KEY))}
      variant="full-screen"
      show={isShowed}
    >
      <ModalHeader label={t('calculator.ui.modal-title')} title={t('calculator.ui.modal-title')} />
      <ModalContent>
        <Remote widget="calculator/root" id="pdp-calculator" data={data} fallback={<Fallback />} />
      </ModalContent>
    </Modal>
  ) : (
    <Drawer
      breakpoint={1200}
      show={isShowed}
      onClose={() => {
        handleOnClose(t(CLOSE_MODAL_KEY));
      }}
      className={drawer}
    >
      <DrawerHeader
        sticky
        onClickBack={() => {
          handleOnClose(t(GO_BACK_KEY));
        }}
        title={t('calculator.ui.modal-title')}
        className={drawerHeader}
      />
      <DrawerContent className={drawerContent}>
        <Remote widget="calculator/root" id="pdp-calculator" data={data} fallback={<Fallback />} />
      </DrawerContent>
    </Drawer>
  );
};

const drawer = css`
  --drawerWidth: 1144px !important;
`;

const drawerHeader = css`
  z-index: 2;

  > span {
    margin: 40px 0;
    font-size: 26px;
    line-height: 32px;

    @media (--only-mobile) {
      margin: 12px 0;
      font-size: 16px;
      line-height: 24px;
    }
  }
`;

const drawerContent = css`
  /* important is used to cover fronton styles according to the design */
  padding: 0 !important;
`;

export const mapStateToProps = (state: RootReducer): CalculatorModalMappedState => {
  const images = getProductImages(state);
  const primaryPrice = getPrimaryPrice(state);
  const secondPrice = getSecondPrice(state);
  const additionalAsMain = getIsPricesSwapped(state);

  const data = {
    productId: getArticle(state),
    displayedName: getDisplayedName(state) || null,
    regionId: getRegionId(state) || null,
    photo: images?.length ? images[0].url : null,
    mainPrice: primaryPrice?.price || null,
    additionalPrice: secondPrice?.price || null,
    mainUomRus: primaryPrice?.unit || null,
    additionalUomRus: secondPrice?.unit || null,
    additionalAsMain,
  };

  return {
    data,
  };
};

export const mapDispatchToProps = (dispatch: Dispatch) => ({
  gaCalculateQuantityClose: ({ elementText }: { elementText: string }) => {
    dispatch(CALCULATE_QUANTITY_GA_CLOSE({ elementText }));
  },
});

export const PdpCalculatorModal = connect(mapStateToProps, mapDispatchToProps)(CalculatorModal);
